import { TTheme } from './themeTypes'

export const baseTheme = {
	extend: {
		keyframes: {
			'fade-in': {
				from: { opacity: '0' },
				to: { opacity: '1' },
			},
			'fade-out': {
				from: { opacity: '1' },
				to: { opacity: '0' },
			},
			'accordion-down': {
				from: { height: '0' },
				to: { height: 'var(--radix-accordion-content-height)' },
			},
			'accordion-up': {
				from: { height: 'var(--radix-accordion-content-height)' },
				to: { height: '0' },
			},
			'collapsible-down': {
				from: { height: '0' },
				to: { height: 'var(--radix-collapsible-content-height)' },
			},
			'collapsible-up': {
				from: { height: 'var(--radix-collapsible-content-height)' },
				to: { height: '0' },
			},
			'pulse-shadow': {
				'0%': { boxShadow: '0 0 0 0 var(--pulse-color-1)' },
				'100%': { boxShadow: '0 0 0 8px var(--pulse-color-2)' },
			},
			'logo-showcase': {
				'0%': { transform: 'translateX(0)' },
				'100%': { transform: 'translateX(-4700px)' },
			},
		},
		animation: {
			'fade-in': 'fade-in 0.2s ease-out',
			'fade-out': 'fade-ut 0.2s ease-out',
			'accordion-down': 'accordion-down 0.2s ease-out',
			'accordion-up': 'accordion-up 0.2s ease-out',
			'collapsible-down': 'collapsible-down 0.2s ease-out',
			'collapsible-up': 'collapsible-up 0.2s ease-out',
			'pulse-shadow': 'pulse-shadow 1.25s cubic-bezier(0.66,0,0,1) infinite',
			'logo-showcase': 'logo-showcase 200s linear infinite',
		},
		maskImage: {
			'horizontal-fade':
				'linear-gradient(to right, transparent, black 22%, black 78%, transparent)',
		},
		spacing: {
			6.5: '1.625rem',
			7.75: '1.938rem',
			8.5: '2.125rem',
			11.5: '2.875rem',
			17.5: '4.375rem',
			18: '4.5rem',
			21: '5.25rem',
			22: '5.5rem',
			25: '6.25rem',
			26: '6.5rem',
			30: '7.5rem',
			45: '11.25rem',
			49: '12.25rem',
			75: '18.75rem',
			76: '19rem',
			100: '25rem',
			'1216px': '1216px',
			'desktop-container': 'min(1216px,calc(100%-64px))',
			'safe-area-bottom': 'env(safe-area-inset-bottom,0)',
		},
		maxWidth: {
			'desktop-container': 'min(1216px, calc(100% - 64px))',
			'qr-screen-constrained': 'calc(600px + 40px)',
			'cabinet-screen-constrained': '1658px',
		},
	},
	screens: {
		mobile: '390px',
		tablet: '740px',
		desktop: '1220px',
		// TODO: update with correct value when provided by designer
		'large-desktop': '1800px',
	},
	borderRadius: {
		none: '0rem',
		'4': '0.25rem',
		'6': '0.375rem',
		'8': '0.5rem',
		'9.5': '0.594rem',
		'10': '0.625rem',
		'12': '0.750rem',
		'16': '1rem',
		full: 'calc(infinity * 1px)',
	},
	boxShadow: {
		DEFAULT: '0px 2px 32px rgba(63, 72, 103, 0.08)',
		icon: '0px 1px 16px rgba(63, 72, 103, 0.13)',
		'cta-container': '0px 1px 8px rgba(63, 72, 103, 0.13)',
		generator: '0px 1px 8px rgba(63, 72, 103, 0.16)',
		input: '0px 0px 8px rgba(73, 129, 255, 0.2)',
		none: 'none',
	},
	dropShadow: {
		DEFAULT: '0px 2px 32px rgba(63, 72, 103, 0.08)',
		icon: '0px 1px 16px rgba(63, 72, 103, 0.13)',
		'cta-container': '0px 1px 8px rgba(63, 72, 103, 0.13)',
		generator: '0px 1px 8px rgba(63, 72, 103, 0.16)',
		input: '0px 0px 8px rgba(73, 129, 255, 0.2)',
		none: 'none',
	},
	colors: {
		transparent: 'transparent',
		white: '#FFFFFF',
		black: '#0A0909',
		'dark-grey': '#3F3E3E',
		grey: '#84868E',
		'border-grey': '#CDD0DB',
		'border-grey-50': '#E3E5ED',
		'light-grey-70': '#F5F6F7B3',
		'light-grey-80': '#F5F6F7B2',
		'logo-grey': '#A1A6B6',
		'generator-bg': '#F5F6FB',
		'plain-text-bg': '#EDEDED',
		'website-bg': '#D6D9FC',
		'popup-overlay': '#212023CC',
		blue: '#4981FF',
		'blue-0': 'rgba(73,129,255,0)',
		'blue-8': '#3EACF614',
		'blue-50': 'rgba(73,129,255,0.5)',
		'blue-hover': '#306BEF',
		'light-blue': '#F7F9FC',
		brown: '#E89E6A',
		error: '#FD4255',
		orange: '#FF9C6F',
		green: '#6AD39F',
		'green-70': '#A2B3AB',
		pink: '#FB86BB',
		'red-50': '#FF7C7C',
		purple: '#9BA2FB',
		'purple-8': '#9BA2FB14',
		'light-purple': '#CBBEFC',
		'dark-purple': '#302c46',
		yellow: '#FEBE4F',
		'yellow-50': '#FFC46C',
		// QR color schemes
		'qr-color-scheme-1': {
			primary: '#6594FF',
			secondary: '#FFFFFF',
		},
		'qr-color-scheme-2': {
			primary: '#ECEDF1',
			secondary: '#232321',
		},
		'qr-color-scheme-3': {
			primary: '#ECECF0',
			secondary: '#6594FF',
		},
		'qr-color-scheme-4': {
			primary: '#DAEBF6',
			secondary: '#6594FF',
		},
		'qr-color-scheme-5': {
			primary: '#B69EDE',
			secondary: '#FFFFFF',
		},
		'qr-color-scheme-6': {
			primary: '#6ECD9D',
			secondary: '#242420',
		},
		'qr-color-scheme-7': {
			primary: '#FACB67',
			secondary: '#FFFFFF',
		},
	},
	fontFamily: {
		body: ['var(--font-body)', 'sans-serif'],
		heading: ['var(--font-heading)', 'var(--font-body)', 'sans-serif'],
		slab: ['var(--font-slab)', 'serif'],
	},
	fontWeight: {
		normal: '400',
		medium: '500',
		semibold: '600',
	},
	fontSize: {
		'heading-0': [
			'var(--font-size-heading-0)',
			{
				fontWeight: '600',
				lineHeight: 'var(--leading-heading-0)',
			},
		],
		'heading-1': [
			'var(--font-size-heading-1)',
			{
				fontWeight: '600',
				lineHeight: 'var(--leading-heading-1)',
			},
		],
		'heading-2': [
			'var(--font-size-heading-2)',
			{
				fontWeight: '600',
				lineHeight: 'var(--leading-heading-2)',
			},
		],
		'heading-3': [
			'var(--font-size-heading-3)',
			{
				fontWeight: '600',
				lineHeight: 'var(--leading-heading-3)',
			},
		],
		'heading-4': [
			'var(--font-size-heading-4)',
			{
				fontWeight: '500',
				lineHeight: 'var(--leading-heading-4)',
			},
		],
		'body-input-title': [
			'1rem',
			{
				lineHeight: '1.5rem',
				fontWeight: '600',
			},
		],
		'body-enlarged-title': [
			'1.5rem',
			{
				lineHeight: '2rem',
				fontWeight: '500',
			},
		],
		'body-enlarged': [
			'1.5rem',
			{
				lineHeight: '2rem',
				fontWeight: '400',
			},
		],
		'body-large': [
			'1.125rem',
			{
				lineHeight: '1.625rem',
				fontWeight: '400',
			},
		],
		'body-medium': [
			'1rem',
			{
				lineHeight: '1.5rem',
				fontWeight: '400',
			},
		],
		'body-small': [
			'0.875rem',
			{
				lineHeight: '1.375rem',
				fontWeight: '400',
			},
		],
		'body-caption': [
			'0.75rem',
			{
				lineHeight: '1.25rem',
				fontWeight: '400',
			},
		],
		'button-small': [
			'0.875rem',
			{
				lineHeight: '1.375rem',
				fontWeight: '500',
			},
		],
		'button-large': [
			'1.125rem',
			{
				lineHeight: '1.625rem',
				fontWeight: '600',
			},
		],
		'10': [
			'0.625rem',
			{
				lineHeight: '1.125rem',
			},
		],
	},
	lineHeight: {
		none: '0px',
		'heading-0': 'var(--leading-heading-0)',
		'heading-1': 'var(--leading-heading-1)',
		'heading-2': 'var(--leading-heading-2)',
		'heading-3': 'var(--leading-heading-3)',
		'heading-4': 'var(--leading-heading-4)',
	},
	type: {
		'heading-0': {
			fontSize: 'heading-0',
			fontFamily: 'heading',
		},
		'heading-1': {
			fontSize: 'heading-1',
			fontFamily: 'heading',
		},
		'heading-2': {
			fontSize: 'heading-2',
			fontFamily: 'heading',
		},
		'heading-3': {
			fontSize: 'heading-3',
			fontFamily: 'heading',
		},
		'heading-4': {
			fontSize: 'heading-4',
			fontFamily: 'heading',
		},
		'body-input-title': {
			fontSize: 'body-input-title',
			fontFamily: 'body',
		},
		'body-enlarged-title': {
			fontSize: 'body-enlarged-title',
			fontFamily: 'heading',
		},
		'body-enlarged': {
			fontSize: 'body-enlarged',
			fontFamily: 'body',
		},
		'body-large': {
			fontSize: 'body-large',
			fontFamily: 'body',
		},
		'body-medium': {
			fontSize: 'body-medium',
			fontFamily: 'body',
		},
		'body-small': {
			fontSize: 'body-small',
			fontFamily: 'body',
		},
		'body-caption': {
			fontSize: 'body-caption',
			fontFamily: 'body',
		},
		'button-small': {
			fontSize: 'button-small',
			fontFamily: 'body',
		},
		'button-large': {
			fontSize: 'button-large',
			fontFamily: 'body',
		},
		'10': {
			fontSize: '10',
			fontFamily: 'body',
		},
	},
	gradient: {
		'white-to-transparent-v':
			'linear-gradient(to bottom, #FFFFFF 0%, transparent 100%)',
		'transparent-to-white-v': 'linear-gradient(to top, #FFFFFF, transparent)',
		'transparent-to-white-h':
			'linear-gradient(to right, transparent 0%, transparent 65%, white 100%)',
		'white-to-transparent-h':
			'linear-gradient(to right, #FFFFFF 0%, transparent 100%)',
		'grey-v': 'linear-gradient(to bottom, #FFFFFF 0%, #F5F6F7 100%)',
		'grey-h': 'linear-gradient(to right, #FFFFFF 0%, #F5F6F7 100%)',
		'grey-to-transparent-v':
			'linear-gradient(to bottom, #CDD0DB 0%, transparent 100%)',
		'transparent-to-grey-v':
			'linear-gradient(to top, #CDD0DB 0%, transparent 100%)',
		'purple-v': 'linear-gradient(to bottom, #FFFFFF 0%, #F1F3FF 100%)',
		'purple-h': 'linear-gradient(to right, #FFFFFF 0%, #F1F3FF 100%)',
		'blue-v':
			'linear-gradient(to bottom, rgba(62, 172, 246, 0.08) 0%, rgba(155, 162, 251, 0.08) 100%)',
		'blue-h':
			'linear-gradient(to right, rgba(62, 172, 246, 0.08) 0%, rgba(155, 162, 251, 0.08) 100%)',
		'blue-purple-grey-v':
			'linear-gradient(to bottom, #F7F9FC 0%, #EBF5FF 50%, #EDEEFF 100%)',
		'blue-purple-grey-h':
			'linear-gradient(to right, #F7F9FC 0%, #EBF5FF 50%, #EDEEFF 100%)',
	},
} as const satisfies TTheme
